import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import SVGInline from "react-svg-inline";

import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';

import { useInstitute, useInstituteQuery } from '../../hooks/useInstitute';
import { useUser } from '../../hooks/useUser';
import Account from './Account';
import ChangePassword from "./ChangePassword";
import InstitutePicker from './InstitutePicker';

const programNavIcon = require('../../../images/progam-nav.svg.raw');
const profilingNavIcon = require('../../../images/profiling-nav.svg.raw');
const reportsNavIcon = require('../../../images/reports-nav.svg.raw');
const usersNavIcon = require('../../../images/users-nav.svg.raw');
const instituteNavIcon = require('../../../images/institute-nav.svg.raw');
const accountNavIcon = require('../../../images/account-nav.svg.raw');
const registrationIcon = require('../../../images/registration-nav.svg.raw');
const homeNavIcon = require('../../../images/home-nav.svg.raw');
const vcmNavIcon = require('../../../images/vcm-nav.svg.raw');

const styles = theme => ({
  header: {
    background: 'white',
    boxSizing: 'border-box',
    paddingLeft: 80,
    paddingRight: 80,
    paddingTop: 10,
    paddingBottom: 10,
    minHeight: 75,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 10,
    boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('md')]: {
      paddingLeft: 20,
      paddingRight: 20
    }
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  nav: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: '10px 20px',
    marginLeft: 'auto'
  },
  menuItem: {
    position: 'relative',
    display: 'block',
    textAlign: 'center',
    minWidth: 60,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.main
    },
    '&.active span': {
      color: theme.palette.secondary.main
    },
    '&.active svg path': {
      fill: theme.palette.secondary.main
    },
    '&.active svg g': {
      stroke: theme.palette.secondary.main
    }
  },
  menuItemImg: {
    display: 'inline-block',
    '& svg path': {
      fill: theme.palette.primary.main
    },
    '& svg g': {
      stroke: theme.palette.primary.main
    }
  }
});

const Header = ({ classes, user, institute, institutes, onInstituteChange, openProfilesCount, pendingRegistrationsCount, vcmsInReviewCount, homeEnabled }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const {UpdateInstituteId} = useInstitute();
  const {data: instituteDetails} = useInstituteQuery(institute.id);
  const {UpdateUser} = useUser();

  useEffect(() => {
    UpdateInstituteId(institute.id);
    UpdateUser(user)
  }, []);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePassword = () => {
    setAnchorEl(null);
    setChangePasswordOpen(true);
  };

  const userIsActive = (match, location) => {
    const urlRegex = /^\/users.*/g;
    const currentLocation = location.pathname;
    const isUser = urlRegex.test(currentLocation);

    if (isUser) {
      return true;
    }
  };

  const registrationsRequireApproval = useMemo(() => {
    if (!instituteDetails) return false;
    return instituteDetails.elective_registrations_require_approval ||
      instituteDetails.learning_journey_registrations_require_approval ||
      instituteDetails.vocational_elective_registrations_require_approval ||
      instituteDetails.vocational_learning_journey_registrations_require_approval;
  }, [instituteDetails]);

  const registrationsEnabled = useMemo(() => {
    return registrationsRequireApproval && (user.is_any_admin || user.is_any_supervisor)
  }, [registrationsRequireApproval, user]);

  const vcmEnabled = useMemo(() => {
    return (user.is_any_admin || user.is_any_supervisor) && instituteDetails?.vcm_enabled;
  }, [instituteDetails, user]);

  const profilingEnabled = user.is_any_admin || user.is_any_supervisor;
  const reportsEnabled = user.is_any_admin ||  user.is_any_supervisor;
  const usersEnabled = user.is_any_admin ||  user.is_any_supervisor;
  const viewInstitutesEnabled = user.is_institute_admin || user.is_super_admin;

  return (
    <div className={classes.header}>
      { user.is_super_admin ?
        (<InstitutePicker institute={institute} institutes={institutes} onInstituteChange={onInstituteChange} />)
      :
      (<img className={classes.logo} style={{height: 35}} src={institute.logo_url} />)
      }
      {/* Rendered in left-to-right visual order (previously reversed because the items were floated right). */}
      <nav className={classes.nav}>
        { homeEnabled && <NavItem classes={classes} to="/home" svg={homeNavIcon}>Home</NavItem> }
        <NavItem classes={classes} to="/programs" svg={programNavIcon}>Programs</NavItem>
        { registrationsEnabled && <NavItem classes={classes} to="/registrations" svg={registrationIcon} notification={pendingRegistrationsCount > 0}>Registrations</NavItem> }
        { profilingEnabled && <NavItem classes={classes} to="/profiling" svg={profilingNavIcon} notification={openProfilesCount > 0}>Profiling</NavItem> }
        { vcmEnabled && <NavItem classes={classes} to="/vcm" svg={vcmNavIcon} notification={vcmsInReviewCount > 0}>VCM</NavItem> }
        { reportsEnabled && <NavItem classes={classes} to="/reports" svg={reportsNavIcon}>Reports</NavItem> }
        { usersEnabled && <NavItem userIsActive={userIsActive} classes={classes} to="/users" svg={usersNavIcon}>Users</NavItem> }
        { viewInstitutesEnabled && <NavItem classes={classes} to={`/institutes/${institute.id}`} svg={instituteNavIcon}>Institute</NavItem>}
        <MenuItem
          classes={classes}
          component='div'
          to="/"
          svg={accountNavIcon}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          Account
        </MenuItem>
      </nav>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose} >
        <Account user={user} sso_enabled={instituteDetails?.sso_enabled} onChangePassword={handleChangePassword}/>
      </Menu>
      <ChangePassword open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)}/>
    </div>
  );
};

const NavItem = (props) => {
  return (
    <NavLink activeClassName='active' className={props.classes.menuItem} to={props.to} isActive={props.userIsActive}>
      { props.notification && <div style={{position: 'absolute', top: 2, left: 12, background: '#f00', width: 9, height: 9, borderRadius: "50%"}}></div> }
      <SVGInline className={props.classes.menuItemImg} svg={props.svg} /><br />
      <Typography component="span" color="primary" style={{display: 'block', fontSize: 13, lineHeight: 1}}>{props.children}</Typography>
    </NavLink>
  );
};

const MenuItem = (props) => {
  return (
    <a className={props.classes.menuItem} onClick={props.onClick}>
      <SVGInline className={props.classes.menuItemImg} svg={props.svg} /><br />
      <Typography component="span" color="primary" style={{display: 'block', fontSize: 13, lineHeight: 1}}>{props.children}</Typography>
    </a>
  );
};

export default withStyles(Header, styles);
